'use client'

import * as React from 'react'
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Command as CommandPrimitive } from 'cmdk'
import { useQuery } from '@tanstack/react-query'
import { getFilterCriteriaCountiesGetFilterCriteriaCountiesPostOptions } from '@/generated/@tanstack/react-query.gen'
import { z } from 'zod'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

const resonseSchema = z.object({
  status: z.string(),
  county_names: z.array(z.string()),
})

interface Props {
  selected: string
  setSelected: (value: string) => void
}

export function ApnSearchCounty({ selected, setSelected }: Props) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState('')

  const { data = [], isLoading } = useQuery({
    ...getFilterCriteriaCountiesGetFilterCriteriaCountiesPostOptions({
      body: {},
    }),
    staleTime: 1000 * 60 * 5,
    select: (data) => {
      return resonseSchema.parse(data).county_names
    },
  })

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current
      if (input) {
        if (e.key === 'Escape') {
          input.blur()
          setOpen(false)
        }
      }
    },
    []
  )

  const handleSelect = (value: string) => {
    setSelected(value)
    setInputValue(value)
    inputRef?.current?.blur()
    setOpen(false)
  }

  const hasFilteredData =
    data.length > 0 &&
    data.filter((item) => item.toLowerCase().includes(inputValue.toLowerCase()))
      .length > 0
  
  React.useEffect(() => {
    inputRef.current?.focus()
  }, [])
  
  return (
    <Command
      onKeyDown={handleKeyDown}
      className="overflow-visible bg-transparent"
    >
      <div className="text-sm">
        <div className="flex flex-col gap-1">
          <div className="relative">
            <CommandPrimitive.Input
              ref={inputRef}
              value={inputValue}
              onValueChange={(v) => {
                setSelected('')
                setInputValue(v)
                if (v !== selected) {
                  setSelected('')
                }
              }}
              onBlur={() => setOpen(false)}
              onFocus={() => setOpen(true)}
              placeholder={'Select a County'}
              className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground rounded-lg w-full
              focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary
              "
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              {isLoading && (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              )}
              {hasFilteredData && (
                <ChevronDown
                  className="h-4 w-4 opacity-50 cursor-pointer"
                  onClick={() => inputRef.current?.focus()}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {open && hasFilteredData ? (
        <div className="relative max-h-[200px] mt-2">
          <CommandList>
            <div className="absolute top-0 z-[9999] w-full rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in">
              <CommandGroup className="h-full overflow-auto max-h-[200px]">
                {data.map((value, index) => {
                  return (
                    <CommandItem
                      value={value}
                      key={`${value}-${index}`}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onSelect={(_) => {
                        handleSelect(value)
                      }}
                      className={cn('cursor-pointer py-2')}
                    >
                      {value}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </div>
          </CommandList>
        </div>
      ) : null}
    </Command>
  )
}
